import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import React from "react";
import useGoogleAutocomplete from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForUsers } from "../../../utils/confighelpers";
import { FcModal, FieldError, FieldLabel, Icon, inputClass } from "./ui";

/**
 * Add a club location (new design). Same request as the old myLocation/AddLocationModal:
 * POST users/addLocation with the Google place's address, house number, city, zip and coordinates,
 * named by the owner. Used by LocationPicker (Add sessions, Change location).
 */

const PIN = (
  <>
    <path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </>
);

/** Builds the addLocation body from a Google prediction + its place details (same fields as TwLocationInput). */
const toLocationBody = (prediction, details, coordinates) => {
  const comps = Array.isArray(details?.address_components) ? details.address_components : [];
  const last = comps[comps.length - 1];
  return {
    addressType: "current",
    address: prediction.description,
    houseNumber: prediction.structured_formatting?.main_text || "",
    city: comps[comps.length - 3]?.long_name || "",
    zip: last && Array.isArray(last.types) && last.types.find((t) => t.startsWith("postal")) ? last.long_name || "" : "",
    location: { type: "Point", coordinates },
  };
};

export default function AddLocationPopup({ open, onClose, onAdded = () => null }) {
  const { addSuccessToast } = useToast();
  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = useGoogleAutocomplete({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API,
  });
  const [query, setQuery] = React.useState("");
  const [place, setPlace] = React.useState(null); // { prediction, body }
  const [name, setName] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [resolving, setResolving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setQuery("");
      setPlace(null);
      setName("");
      setErrors({});
      setSaving(false);
    }
  }, [open]);

  const pick = (prediction) => {
    if (!prediction?.place_id || !placesService) return;
    setResolving(true);
    placesService.getDetails({ placeId: prediction.place_id }, (details) => {
      setResolving(false);
      if (!details?.geometry?.location) {
        setErrors({ address: "Couldn't load this address. Try another one." });
        return;
      }
      const coordinates = [details.geometry.location.lat(), details.geometry.location.lng()];
      setPlace({ prediction, body: toLocationBody(prediction, details, coordinates) });
      setName((n) => n || prediction.structured_formatting?.main_text || "");
      setErrors({});
    });
    setQuery("");
  };

  const save = async () => {
    const e = {};
    if (!place) e.address = "Search and pick the address from the list.";
    if (!name.trim()) e.name = "Give this location a name members will recognise.";
    setErrors(e);
    if (Object.keys(e).length) return;
    const body = { ...place.body, addressName: name.trim() };
    setSaving(true);
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForUsers()}addLocation`, data: body });
      if (res?.data?.statusCode === 200) {
        addSuccessToast(`${body.addressName} added`);
        onAdded(body);
        onClose();
      } else {
        setErrors({ form: typeof res?.data?.message === "string" ? res.data.message : "Couldn't add this location." });
      }
    } catch (err) {
      setErrors({ form: "Couldn't reach the server. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="NEW LOCATION"
      title="Add a location"
      subtitle="Members see the name and address on the session and in maps."
      primaryLabel="Add location"
      onPrimary={save}
      busy={saving}
    >
      {errors.form && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
          {errors.form}
        </div>
      )}

      <div>
        <FieldLabel htmlFor="fc-loc-search">Address</FieldLabel>
        {place ? (
          <div className="flex items-center gap-3 rounded-2xl border border-indigo-bz bg-[#FBF8FE] p-3 ring-2 ring-[#F3E9FF]">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-bz text-white">
              <Icon className="h-5 w-5" d={PIN} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-extrabold text-gray-900">{place.prediction.structured_formatting?.main_text || place.body.address}</span>
              <span className="block truncate text-xs text-gray-500">{place.prediction.structured_formatting?.secondary_text || place.body.city}</span>
            </span>
            <button type="button" onClick={() => setPlace(null)} className="shrink-0 rounded-xl px-3 py-2 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
              Change
            </button>
          </div>
        ) : (
          <Combobox value={null} onChange={pick}>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
                <Icon className="h-5 w-5" d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
              </span>
              <ComboboxInput
                id="fc-loc-search"
                autoComplete="off"
                placeholder="Search street, venue or postcode"
                className={classNames(inputClass(errors.address), "pl-11")}
                displayValue={() => query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  getPlacePredictions({ input: e.target.value });
                }}
              />
              {(resolving || isPlacePredictionsLoading) && (
                <span className="absolute inset-y-0 right-4 flex items-center">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-bz" />
                </span>
              )}
            </div>
            {query.trim() !== "" && (
              <ComboboxOptions
                anchor="bottom start"
                className="z-[70] mt-1 max-h-64 w-[var(--input-width)] overflow-auto rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl empty:invisible focus:outline-none"
              >
                {placePredictions.map((p) => (
                  <ComboboxOption
                    key={p.place_id}
                    value={p}
                    className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 data-[focus]:bg-[#F7F4F8]"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FFE8EB] text-portland-orange">
                      <Icon className="h-4 w-4" d={PIN} />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold text-gray-900">{p.structured_formatting?.main_text || p.description}</span>
                      <span className="block truncate text-xs text-gray-500">{p.structured_formatting?.secondary_text || ""}</span>
                    </span>
                  </ComboboxOption>
                ))}
              </ComboboxOptions>
            )}
          </Combobox>
        )}
        <FieldError>{errors.address}</FieldError>
      </div>

      <div>
        <FieldLabel htmlFor="fc-loc-name">Location name</FieldLabel>
        <input
          id="fc-loc-name"
          type="text"
          value={name}
          maxLength={60}
          placeholder="e.g. Studio Noord, Vondelpark"
          onChange={(e) => {
            setName(e.target.value);
            setErrors((x) => ({ ...x, name: "" }));
          }}
          className={inputClass(errors.name)}
        />
        <FieldError>{errors.name}</FieldError>
      </div>
    </FcModal>
  );
}
